/**
 * `/api/v1/migration/*` — moving an account off Cloudinary or ImageKit.
 *
 * The SaaS backend is an authorization layer here rather than the implementation:
 * it resolves the caller's account server-side and forwards to the
 * `migration-onramp` service, which owns the migration tables, the URL translator
 * and the library walk. Two consequences for callers on this side:
 *
 *  - There is no `group_id` parameter anywhere below, and there must never be
 *    one. The account is decided from the session.
 *  - A 503 means "this deployment has no migration-onramp", not "something broke". The
 *    page renders that as a feature-unavailable state.
 */
import { FetchResult } from 'src/types/TApiQueries';
import {
  MigrationBrokenList,
  MigrationChallenge,
  MigrationKeyType,
  MigrationOverview,
  MigrationOriginTestResult,
  MigrationProvider,
  MigrationRun,
  MigrationRunOptions,
  MigrationScanResult,
  MigrationVerifyMethod,
  MigrationVerifyResult,
} from 'src/types/TMigration';
import { config } from 'src/utils/config';
import { getStandardHeaders, performFetchWithLogging } from '../utils/helpers';

const base = () => `${config.hostUrl}/api/v1/migration`;

export async function getMigrationOverview(
  accessToken: string,
): Promise<FetchResult<MigrationOverview>> {
  return performFetchWithLogging(base(), {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

/** Clear the provider, delivery URL, credentials and identifiers.
 *
 *  Copied images are deliberately NOT removed — they are assets the customer
 *  pays to store. Re-registering the same vendor finds them again. */
export async function deleteMigrationSetup(
  accessToken: string,
): Promise<FetchResult<MigrationOverview>> {
  return performFetchWithLogging(base(), {
    method: 'DELETE',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export type SetProviderPayload = {
  provider: MigrationProvider;
  /** Their delivery base, e.g. `https://res.cloudinary.com/acme`. */
  base_url?: string;
  /**
   * Lazy pull-through: assets arrive as visitors ask for them. Off while the
   * customer is still setting up — being registered is not the same as being
   * ready for traffic.
   */
  pull_enabled?: boolean;
};

export async function setMigrationProvider(
  data: SetProviderPayload,
  accessToken: string,
): Promise<FetchResult<MigrationOverview>> {
  return performFetchWithLogging(`${base()}/provider`, {
    method: 'PUT',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(data),
  });
}

export type KeyPayload = { key_type: MigrationKeyType; value: string };

export async function addMigrationKey(
  data: KeyPayload,
  accessToken: string,
): Promise<FetchResult<MigrationOverview>> {
  return performFetchWithLogging(`${base()}/keys`, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(data),
  });
}

export async function removeMigrationKey(
  data: KeyPayload,
  accessToken: string,
): Promise<FetchResult<MigrationOverview>> {
  return performFetchWithLogging(`${base()}/keys`, {
    method: 'DELETE',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(data),
  });
}

export async function challengeMigrationKey(
  data: KeyPayload,
  accessToken: string,
): Promise<FetchResult<MigrationChallenge>> {
  return performFetchWithLogging(`${base()}/keys/challenge`, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(data),
  });
}

export async function verifyMigrationKey(
  data: KeyPayload & { method: MigrationVerifyMethod },
  accessToken: string,
): Promise<FetchResult<MigrationVerifyResult>> {
  return performFetchWithLogging(`${base()}/keys/verify`, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(data),
  });
}

export type SetOriginPayload = {
  base_url?: string;
  /**
   * Omit to leave the stored credential untouched; send `''` to clear it. The
   * form renders a masked hint rather than the value, so it must never post
   * the mask back as the new secret.
   */
  api_key?: string;
  api_secret?: string;
  pull_rps?: number;
};

export async function setMigrationOrigin(
  data: SetOriginPayload,
  accessToken: string,
): Promise<FetchResult<MigrationOverview>> {
  return performFetchWithLogging(`${base()}/origin`, {
    method: 'PUT',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(data),
  });
}

/**
 * Test credentials against the provider.
 *
 * With `credentials`, those are tested and **nothing is stored** — so "does
 * this key work?" is answerable before "keep this key". Without, whatever is
 * already saved is tested.
 */
export async function testMigrationOrigin(
  accessToken: string,
  credentials?: { api_key?: string; api_secret?: string; base_url?: string; provider?: string },
): Promise<FetchResult<MigrationOriginTestResult>> {
  return performFetchWithLogging(`${base()}/origin/test`, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(credentials || {}),
  });
}

export async function getMigrationBrokenAssets(
  accessToken: string,
): Promise<FetchResult<MigrationBrokenList>> {
  return performFetchWithLogging(`${base()}/broken`, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function scanMigrationUrls(
  urls: string,
  accessToken: string,
): Promise<FetchResult<MigrationScanResult>> {
  return performFetchWithLogging(`${base()}/scan`, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({ urls }),
  });
}

export async function startMigrationCopy(
  options: MigrationRunOptions,
  accessToken: string,
): Promise<FetchResult<MigrationRun>> {
  return performFetchWithLogging(`${base()}/copy`, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(options),
  });
}

export async function getMigrationCopy(
  runId: number,
  accessToken: string,
): Promise<FetchResult<MigrationRun>> {
  return performFetchWithLogging(`${base()}/copy/${runId}`, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function cancelMigrationCopy(
  runId: number,
  accessToken: string,
): Promise<FetchResult<{ cancelling: number }>> {
  return performFetchWithLogging(`${base()}/copy/${runId}/cancel`, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}
